import { useEffect, useId, useRef } from "react";
import CloseIcon from "../icons/CloseIcon";
import Portal from "../portal/Portal";
import styles from "./dialog.module.css";

// Open dialogs, top last: Escape only closes the top one (a confirmation
// opened over a form closes alone). While any is open the page behind doesn't
// scroll.
const openDialogs = [];
let pageOverflowBefore = "";

/**
 * The one popup shell every dashboard popup uses: backdrop, card, header
 * (optional icon, title, subtitle, close), scrolling body, footer actions.
 * Rendered in a portal so no parent's overflow / transform can clip it.
 *
 * - size: "sm" (420) · "md" (560) · "lg" (760) · "xl" (1040)
 * - tone: tints the icon — "neutral" · "info" · "danger" · "success"
 * - closeOnEscape / closeOnBackdrop: off by default — most popups hold forms
 *   a stray key or click would throw away.
 * - scrollBody: false for a body with inline dropdowns that must not be
 *   clipped (the dialog then grows instead of scrolling).
 * - initialFocusRef: what gets focus on open (default: the card itself).
 * - bodyDir: "rtl" (default) · "ltr" for content laid out for a left-to-right
 *   parent (e.g. row-reverse rows); the header and footer are always RTL.
 */
const Dialog = ({
  title,
  description = "",
  icon = null,
  tone = "neutral",
  size = "md",
  onClose,
  footer = null,
  children,
  closeOnEscape = false,
  closeOnBackdrop = false,
  scrollBody = true,
  role = "dialog",
  initialFocusRef = null,
  bodyDir = "rtl",
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const cardRef = useRef(null);
  const pressedOnBackdrop = useRef(false);
  // Read at key time: a dialog can turn these off while a request runs.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeOnEscapeRef = useRef(closeOnEscape);
  closeOnEscapeRef.current = closeOnEscape;

  useEffect(() => {
    const token = {};
    if (!openDialogs.length) {
      pageOverflowBefore = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openDialogs.push(token);
    const previouslyFocused = document.activeElement;
    (initialFocusRef?.current || cardRef.current)?.focus();

    const onKeyDown = (event) => {
      if (event.key !== "Escape" || !closeOnEscapeRef.current) return;
      if (openDialogs[openDialogs.length - 1] !== token) return;
      event.stopPropagation();
      onCloseRef.current?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      openDialogs.splice(openDialogs.indexOf(token), 1);
      if (!openDialogs.length)
        document.body.style.overflow = pageOverflowBefore;
      if (
        previouslyFocused instanceof HTMLElement &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
    // Open/close only; the handler reads the latest props through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Portal>
      <div
        // The tables page's drag-to-select box ignores anything inside this.
        data-no-select-box
        className={`${styles.backdrop} ${role === "alertdialog" ? styles.alertLayer : ""}`}
        onMouseDown={(event) => {
          pressedOnBackdrop.current = event.target === event.currentTarget;
        }}
        onClick={(event) => {
          // Pressed and released on the backdrop — a text selection dragged
          // out of the card doesn't count.
          if (
            closeOnBackdrop &&
            pressedOnBackdrop.current &&
            event.target === event.currentTarget
          ) {
            onClose?.();
          }
        }}
      >
        <div
          ref={cardRef}
          className={`${styles.dialog} ${styles[size] || styles.md}`}
          role={role}
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          tabIndex={-1}
          dir="rtl"
        >
          <header className={styles.header}>
            {icon && (
              <span
                className={`${styles.badge} ${styles[`tone-${tone}`] || ""}`}
              >
                {icon}
              </span>
            )}
            <div className={styles.headings}>
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className={styles.description}>
                  {description}
                </p>
              )}
            </div>
            {onClose && (
              <button
                type="button"
                className={styles.close}
                aria-label="إغلاق"
                onClick={onClose}
              >
                <CloseIcon />
              </button>
            )}
          </header>
          <div
            dir={bodyDir}
            className={`${styles.body} ${scrollBody ? styles.scrollBody : ""}`}
          >
            {children}
          </div>
          {footer && <footer className={styles.footer}>{footer}</footer>}
        </div>
      </div>
    </Portal>
  );
};

export default Dialog;
